import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { notify, getAdminProfile } from '../../utils/notifications';
import { sendSMS, buildNewLeadSMS } from '../../services/smsService';

const generateDocumentsFromLead = async (supabase, orgId, jobId, job, contact, orgName) => {
  const now = new Date();
  const dueDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  const laborCost = Number(job?.labor_cost) || 0;
  const materialsCost = Number(job?.materials_cost) || 0;
  const subtotal = laborCost + materialsCost;
  const taxRate = 0;
  const tax = 0;
  const total = subtotal + tax;
  const clientName = contact ? `${contact?.first_name} ${contact?.last_name}`?.trim() : 'Client';
  const pickupAddr = job?.custom_fields?.pickup_address || job?.custom_fields?.move_from || '';
  const deliveryAddr = job?.custom_fields?.delivery_address || job?.custom_fields?.move_to || '';
  const locationStr = pickupAddr && deliveryAddr
    ? `From: ${pickupAddr} → To: ${deliveryAddr}`
    : pickupAddr || deliveryAddr || job?.location || '';

  const docs = [
    {
      org_id: orgId,
      job_id: jobId,
      doc_type: 'agreement',
      status: 'draft',
      content: {
        title: 'Service Agreement',
        job_title: job?.title,
        client_name: clientName,
        service_description: job?.description || job?.service_type,
        location: locationStr,
        scheduled_date: job?.scheduled_date || '',
        total_cost: total?.toFixed(2),
        company_name: orgName,
        terms: 'Standard service terms apply. Payment due upon completion. Service Provider is not responsible for pre-existing conditions. Client agrees to provide safe access to work area.'
      }
    },
    {
      org_id: orgId,
      job_id: jobId,
      doc_type: 'estimate',
      status: 'draft',
      content: {
        title: 'Estimate',
        estimate_number: `EST-${Date.now()?.toString()?.slice(-6)}`,
        job_title: job?.title,
        client_name: clientName,
        service_description: job?.description || job?.service_type,
        location: locationStr,
        date: now?.toISOString()?.split('T')?.[0],
        service_price: laborCost?.toFixed(2),
        materials_cost: materialsCost?.toFixed(2),
        subtotal: subtotal?.toFixed(2),
        tax: tax?.toFixed(2),
        tax_rate: taxRate,
        total: total?.toFixed(2),
        company_name: orgName
      }
    },
    {
      org_id: orgId,
      job_id: jobId,
      doc_type: 'invoice',
      status: 'unpaid',
      content: {
        title: 'Invoice',
        invoice_number: `INV-${Date.now()?.toString()?.slice(-6)}`,
        job_title: job?.title,
        client_name: clientName,
        service_description: job?.description || job?.service_type,
        location: locationStr,
        date: now?.toISOString()?.split('T')?.[0],
        due_date: dueDate?.toISOString()?.split('T')?.[0],
        duration: job?.duration_hours || 1,
        service_price: laborCost?.toFixed(2),
        materials_cost: materialsCost?.toFixed(2),
        subtotal: subtotal?.toFixed(2),
        tax: tax?.toFixed(2),
        tax_rate: taxRate,
        total: total?.toFixed(2),
        payment_status: 'Unpaid',
        company_name: orgName
      }
    }
  ];

  await supabase?.from('documents')?.insert(docs);
};

const COLUMNS = [
  { id: 'new', label: 'New', color: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' },
  { id: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-400' },
  { id: 'qualified', label: 'Qualified', color: 'bg-[#DCFCE7] text-[#16A34A]', dot: 'bg-[#22C55E]' },
  { id: 'converted', label: 'Converted', color: 'bg-green-100 text-green-700', dot: 'bg-green-400' },
  { id: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700', dot: 'bg-red-400' }
];

const SERVICE_TYPES = ['Plumbing', 'HVAC', 'Electrical', 'Handyman', 'Cleaning', 'Landscaping', 'Pest Control', 'Roofing', 'Painting', 'Pool Service', 'Moving', 'General Contractor', 'Appliance Repair', 'Flooring', 'Other', 'General', 'local_move', 'long_distance', 'commercial_move', 'junk_removal', 'storage'];
const SOURCES = ['Web', 'Referral', 'Phone', 'Walk-in', 'Facebook', 'Google', 'Yelp', 'Nextdoor', 'Other', 'Loaderz Website (API)', 'API'];

// Custom fields per service type (niche-specific, no generic Service Type dropdown)
const SERVICE_CUSTOM_FIELDS = {
  Moving: [
    { key: 'move_from', label: 'Moving From (City/State)', type: 'text', placeholder: 'e.g. Los Angeles, CA' },
    { key: 'move_to', label: 'Moving To (City/State)', type: 'text', placeholder: 'e.g. San Diego, CA' },
    { key: 'move_date', label: 'Desired Move Date', type: 'date' },
    { key: 'home_size', label: 'Home Size', type: 'select', options: ['Studio', '1 Bedroom', '2 Bedrooms', '3 Bedrooms', '4+ Bedrooms', 'Office/Commercial'] },
    { key: 'has_stairs', label: 'Stairs / Elevator?', type: 'select', options: ['No stairs', 'Stairs at origin', 'Stairs at destination', 'Both locations have stairs', 'Elevator available'] },
    { key: 'storage_needed', label: 'Storage Needed?', type: 'select', options: ['No', 'Short-term (< 1 month)', 'Long-term (1+ months)'] },
  ],
  Plumbing: [
    { key: 'issue_type', label: 'Issue Type', type: 'select', options: ['Leak', 'Clog', 'Water heater', 'Pipe installation', 'Drain cleaning', 'Sewer line', 'Emergency', 'Other'] },
    { key: 'urgency', label: 'Urgency', type: 'select', options: ['Emergency (same day)', 'Urgent (within 24h)', 'Normal (this week)', 'Flexible'] },
    { key: 'property_type', label: 'Property Type', type: 'select', options: ['Residential', 'Commercial', 'Multi-family'] },
  ],
  HVAC: [
    { key: 'issue_type', label: 'Issue Type', type: 'select', options: ['AC not cooling', 'Heater not working', 'Installation', 'Maintenance/Tune-up', 'Duct cleaning', 'Other'] },
    { key: 'system_age', label: 'System Age', type: 'select', options: ['< 5 years', '5-10 years', '10-15 years', '15+ years', 'Unknown'] },
    { key: 'urgency', label: 'Urgency', type: 'select', options: ['Emergency', 'Urgent (24h)', 'Normal', 'Flexible'] },
  ],
  Electrical: [
    { key: 'issue_type', label: 'Issue Type', type: 'select', options: ['Panel upgrade', 'Outlet/switch', 'Lighting', 'EV charger', 'Generator', 'Wiring', 'Emergency', 'Other'] },
    { key: 'urgency', label: 'Urgency', type: 'select', options: ['Emergency', 'Urgent (24h)', 'Normal', 'Flexible'] },
    { key: 'property_type', label: 'Property Type', type: 'select', options: ['Residential', 'Commercial', 'Industrial'] },
  ],
  Handyman: [
    { key: 'task_type', label: 'Task Type', type: 'select', options: ['Furniture assembly', 'TV mounting', 'Door/window repair', 'Drywall patch', 'Caulking/sealing', 'Shelf installation', 'Gutter cleaning', 'Deck repair', 'Fence repair', 'Tile repair', 'Painting touch-up', 'Weatherstripping', 'Lock & hardware', 'General repairs', 'Other'] },
    { key: 'num_tasks', label: 'Number of Tasks', type: 'select', options: ['1 task', '2-3 tasks', '4-5 tasks', '6+ tasks (half day)', 'Full day of tasks'] },
    { key: 'urgency', label: 'Urgency', type: 'select', options: ['ASAP (same day)', 'This week', 'Next week', 'Flexible'] },
    { key: 'property_type', label: 'Property Type', type: 'select', options: ['Residential', 'Commercial', 'Rental property', 'Airbnb/Vacation rental'] },
    { key: 'materials_needed', label: 'Materials Needed?', type: 'select', options: ['No — I have materials', 'Yes — please bring materials', 'Not sure yet'] },
    { key: 'access_instructions', label: 'Access Instructions', type: 'text', placeholder: 'e.g. Gate code, key under mat, call on arrival' },
  ],
  Cleaning: [
    { key: 'cleaning_type', label: 'Cleaning Type', type: 'select', options: ['Regular/Recurring', 'Deep clean', 'Move-in/Move-out', 'Post-construction', 'Commercial', 'Airbnb/Vacation rental'] },
    { key: 'frequency', label: 'Frequency', type: 'select', options: ['One-time', 'Weekly', 'Bi-weekly', 'Monthly'] },
    { key: 'home_size', label: 'Home/Space Size', type: 'select', options: ['Studio', '1 bed/1 bath', '2 bed/2 bath', '3 bed/2 bath', '4+ bed', 'Commercial space'] },
    { key: 'has_pets', label: 'Pets?', type: 'select', options: ['No pets', 'Dogs', 'Cats', 'Multiple pets'] },
  ],
  Landscaping: [
    { key: 'service_needed', label: 'Service Needed', type: 'select', options: ['Lawn mowing', 'Landscaping design', 'Tree trimming', 'Irrigation', 'Sod installation', 'Cleanup', 'Ongoing maintenance'] },
    { key: 'yard_size', label: 'Yard Size', type: 'select', options: ['Small (< 1/4 acre)', 'Medium (1/4 - 1/2 acre)', 'Large (1/2 - 1 acre)', 'Very large (1+ acre)'] },
    { key: 'frequency', label: 'Frequency', type: 'select', options: ['One-time', 'Weekly', 'Bi-weekly', 'Monthly'] },
  ],
  Roofing: [
    { key: 'issue_type', label: 'Issue Type', type: 'select', options: ['Leak repair', 'Full replacement', 'Inspection', 'Storm damage', 'Gutter repair', 'New installation'] },
    { key: 'roof_type', label: 'Roof Type', type: 'select', options: ['Asphalt shingles', 'Metal', 'Tile', 'Flat/TPO', 'Wood shake', 'Unknown'] },
    { key: 'insurance_claim', label: 'Insurance Claim?', type: 'select', options: ['No', 'Yes - already filed', 'Yes - need help filing'] },
  ],
  'Pest Control': [
    { key: 'pest_type', label: 'Pest Type', type: 'select', options: ['Ants', 'Roaches', 'Termites', 'Rodents', 'Bed bugs', 'Wasps/Bees', 'Spiders', 'General pest', 'Other'] },
    { key: 'service_plan', label: 'Service Plan', type: 'select', options: ['One-time treatment', 'Recurring plan', 'Inspection only', 'Termite inspection'] },
    { key: 'property_type', label: 'Property Type', type: 'select', options: ['Residential', 'Commercial', 'Restaurant/Food service'] },
  ],
  Painting: [
    { key: 'paint_type', label: 'Paint Type', type: 'select', options: ['Interior', 'Exterior', 'Both interior & exterior', 'Cabinet painting', 'Deck/fence staining'] },
    { key: 'rooms', label: 'Rooms / Area', type: 'text', placeholder: 'e.g. Living room, 2 bedrooms' },
    { key: 'sq_footage', label: 'Square Footage', type: 'text', placeholder: 'e.g. 1200 sq ft' },
  ],
  'Pool Service': [
    { key: 'service_type_pool', label: 'Service Type', type: 'select', options: ['Weekly maintenance', 'One-time cleaning', 'Equipment repair', 'Opening/Closing', 'Chemical balance', 'New installation'] },
    { key: 'pool_type', label: 'Pool Type', type: 'select', options: ['In-ground', 'Above-ground', 'Spa/Hot tub', 'Pool + Spa'] },
  ],
  'General Contractor': [
    { key: 'project_type', label: 'Project Type', type: 'select', options: ['Kitchen remodel', 'Bathroom remodel', 'Room addition', 'Basement finish', 'Deck/Patio', 'Whole home renovation', 'Commercial build-out', 'Other'] },
    { key: 'project_scope', label: 'Project Scope', type: 'select', options: ['Small (< $5k)', 'Medium ($5k-$25k)', 'Large ($25k-$100k)', 'Major ($100k+)'] },
    { key: 'timeline', label: 'Timeline', type: 'select', options: ['ASAP', '1-3 months', '3-6 months', '6+ months', 'Flexible'] },
  ],
};

const SOURCE_COLORS = {
  Web: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  Referral: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  Phone: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  'Walk-in': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  Other: 'bg-muted text-muted-foreground'
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr)?.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const STATUS_COLORS = {
  new: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
  contacted: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  qualified: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  converted: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  lost: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
};

const LeadCard = ({ lead, index, onConvert, onDelete, onClick }) => (
  <Draggable draggableId={lead?.id} index={index}>
    {(provided, snapshot) => (
      <div
        ref={provided?.innerRef}
        {...provided?.draggableProps}
        {...provided?.dragHandleProps}
        onClick={() => onClick(lead)}
        className={`bg-card border border-border rounded-lg p-3 mb-2 cursor-pointer active:cursor-grabbing transition-shadow ${
          snapshot?.isDragging ? 'shadow-lg ring-2 ring-primary/30' : 'hover:shadow-md'
        }`}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{lead?.name}</p>
            {lead?.phone && (
              <p className="text-xs text-muted-foreground mt-0.5">{lead?.phone}</p>
            )}
            {lead?.email && (
              <p className="text-xs text-muted-foreground truncate">{lead?.email}</p>
            )}
          </div>
          <div className="flex items-center space-x-1 ml-2">
            {lead?.status !== 'converted' && lead?.status !== 'lost' && (
              <button
                onClick={(e) => { e?.stopPropagation(); onConvert(lead); }}
                className="p-1 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded transition-colors"
                title="Convert to Job"
              >
                <Icon name="UserCheck" size={14} />
              </button>
            )}
            <button
              onClick={(e) => { e?.stopPropagation(); onDelete(lead?.id); }}
              className="p-1 text-muted-foreground hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
              title="Delete"
            >
              <Icon name="Trash2" size={14} />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-1">
          <div className="flex items-center flex-wrap gap-1">
            {lead?.source && (
              <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${SOURCE_COLORS?.[lead?.source] || 'bg-muted text-muted-foreground'}`}>
                {lead?.source}
              </span>
            )}
            {lead?.service_type && (
              <span className="px-1.5 py-0.5 text-xs bg-muted text-muted-foreground rounded-full">
                {lead?.service_type}
              </span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">{timeAgo(lead?.created_at)}</span>
        </div>
      </div>
    )}
  </Draggable>
);

// Modal to confirm conversion — pre-fills all lead data, lets user set date/time/price
const ConvertLeadModal = ({ lead, onClose, onConfirm, saving }) => {
  const today = new Date()?.toISOString()?.split('T')?.[0];
  const [form, setForm] = useState({
    scheduled_date: lead?.custom_fields?.move_date || lead?.scheduled_date || today,
    scheduled_time: lead?.scheduled_time || '09:00',
    labor_cost: lead?.labor_cost || lead?.service_price || '',
    materials_cost: lead?.materials_cost || '',
    duration_hours: lead?.duration_hours || 1,
    notes: lead?.notes || '',
    location: lead?.pickup_address || lead?.location || '',
  });

  if (!lead) return null;

  const inputCls = 'w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e?.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Convert Lead to Job</h2>
            <p className="text-sm text-muted-foreground mt-0.5">{lead?.name} · {lead?.service_type}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Lead summary */}
        <div className="bg-muted/50 rounded-lg p-3 mb-4 space-y-1">
          {lead?.phone && <p className="text-xs text-muted-foreground"><span className="font-medium">Phone:</span> {lead?.phone}</p>}
          {lead?.email && <p className="text-xs text-muted-foreground"><span className="font-medium">Email:</span> {lead?.email}</p>}
          {lead?.pickup_address && <p className="text-xs text-muted-foreground"><span className="font-medium">Pickup:</span> {lead?.pickup_address}</p>}
          {lead?.delivery_address && <p className="text-xs text-muted-foreground"><span className="font-medium">Delivery:</span> {lead?.delivery_address}</p>}
          {lead?.source && <p className="text-xs text-muted-foreground"><span className="font-medium">Source:</span> {lead?.source}</p>}
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Scheduled Date *</label>
              <input
                type="date"
                value={form?.scheduled_date}
                onChange={e => setForm(p => ({ ...p, scheduled_date: e?.target?.value }))}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Scheduled Time</label>
              <input
                type="time"
                value={form?.scheduled_time}
                onChange={e => setForm(p => ({ ...p, scheduled_time: e?.target?.value }))}
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Service Price ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={form?.labor_cost}
                onChange={e => setForm(p => ({ ...p, labor_cost: e?.target?.value }))}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Materials Cost ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={form?.materials_cost}
                onChange={e => setForm(p => ({ ...p, materials_cost: e?.target?.value }))}
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Duration (hours)</label>
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={form?.duration_hours}
                onChange={e => setForm(p => ({ ...p, duration_hours: e?.target?.value }))}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Location</label>
              <input
                type="text"
                placeholder="Job address"
                value={form?.location}
                onChange={e => setForm(p => ({ ...p, location: e?.target?.value }))}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Notes</label>
            <textarea
              rows={2}
              value={form?.notes}
              onChange={e => setForm(p => ({ ...p, notes: e?.target?.value }))}
              className={`${inputCls} resize-none`}
            />
          </div>

          {form?.labor_cost && (
            <div className="bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <p className="text-xs font-semibold text-green-700 dark:text-green-400">
                Total: ${(Number(form?.labor_cost || 0) + Number(form?.materials_cost || 0))?.toFixed(2)}
              </p>
            </div>
          )}
        </div>

        <div className="flex space-x-3 mt-5">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-border text-foreground text-sm font-medium rounded-lg hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(form)}
            disabled={saving || !form?.scheduled_date}
            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <Icon name="UserCheck" size={15} />
            <span>{saving ? 'Creating...' : 'Create Job'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const LeadDetailModal = ({ lead, onClose, onConvert, onDelete, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (lead) {
      setForm({
        name: lead?.name || '',
        phone: lead?.phone || '',
        email: lead?.email || '',
        service_type: lead?.service_type || '',
        source: lead?.source || '',
        status: lead?.status || 'new',
        notes: lead?.notes || '',
        pickup_address: lead?.pickup_address || '',
        delivery_address: lead?.delivery_address || '',
        move_type: lead?.move_type || 'local',
        cubic_feet: lead?.cubic_feet || '',
        miles: lead?.miles || '',
      });
      setIsEditing(false);
    }
  }, [lead]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const isMoving = (lead?.service_type || '')?.toLowerCase()?.includes('mov');
      const { error } = await supabase?.from('leads')?.update({
        name: form?.name,
        phone: form?.phone || null,
        email: form?.email || null,
        service_type: form?.service_type || null,
        source: form?.source || null,
        status: form?.status,
        notes: form?.notes || null,
        pickup_address: form?.pickup_address || null,
        delivery_address: form?.delivery_address || null,
        move_type: isMoving ? (form?.move_type || 'local') : null,
        cubic_feet: (isMoving && form?.move_type === 'long_distance') ? (Number(form?.cubic_feet) || null) : null,
        miles: (isMoving && form?.move_type === 'long_distance') ? (Number(form?.miles) || null) : null,
      })?.eq('id', lead?.id);
      if (error) throw error;
      toast?.success('Lead updated!');
      onUpdate?.({ ...lead, ...form });
      setIsEditing(false);
    } catch (err) {
      toast?.error('Failed to update: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return null;

  const inputCls = 'w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e?.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-foreground">Lead Details</h2>
          <div className="flex items-center space-x-2">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Icon name="Pencil" size={13} />
                <span>Edit</span>
              </button>
            ) : (
              <button
                onClick={() => { setIsEditing(false); setForm({ name: lead?.name || '', phone: lead?.phone || '', email: lead?.email || '', service_type: lead?.service_type || '', source: lead?.source || '', status: lead?.status || 'new', notes: lead?.notes || '', pickup_address: lead?.pickup_address || '', delivery_address: lead?.delivery_address || '' }); }}
                className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="X" size={13} />
                <span>Cancel</span>
              </button>
            )}
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {isEditing ? (
            <>
              <div>
                <label className="block text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Name *</label>
                <input type="text" value={form?.name} onChange={e => setForm(p => ({ ...p, name: e?.target?.value }))} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Phone</label>
                  <input type="tel" value={form?.phone} onChange={e => setForm(p => ({ ...p, phone: e?.target?.value }))} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Email</label>
                  <input type="email" value={form?.email} onChange={e => setForm(p => ({ ...p, email: e?.target?.value }))} className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Service Type</label>
                  <select value={form?.service_type} onChange={e => setForm(p => ({ ...p, service_type: e?.target?.value }))} className={inputCls}>
                    <option value="">— None —</option>
                    {/* Include current DB value if not in list */}
                    {form?.service_type && !SERVICE_TYPES?.includes(form?.service_type) && (
                      <option value={form?.service_type}>{form?.service_type}</option>
                    )}
                    {SERVICE_TYPES?.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Source</label>
                  <select value={form?.source} onChange={e => setForm(p => ({ ...p, source: e?.target?.value }))} className={inputCls}>
                    <option value="">— None —</option>
                    {/* Include current DB value if not in list */}
                    {form?.source && !SOURCES?.includes(form?.source) && (
                      <option value={form?.source}>{form?.source}</option>
                    )}
                    {SOURCES?.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Pickup Address</label>
                <input type="text" value={form?.pickup_address} onChange={e => setForm(p => ({ ...p, pickup_address: e?.target?.value }))} className={inputCls} placeholder="e.g. 123 Main St, Los Angeles, CA" />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Delivery Address</label>
                <input type="text" value={form?.delivery_address} onChange={e => setForm(p => ({ ...p, delivery_address: e?.target?.value }))} className={inputCls} placeholder="e.g. 456 Oak Ave, San Diego, CA" />
              </div>
              {/* Move Type + Long Distance fields — shown when service type is Moving */}
              {(form?.service_type?.toLowerCase()?.includes('mov') || form?.pickup_address || form?.delivery_address) && (
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Move Type</label>
                    <div className="flex rounded-lg border border-border overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setForm(p => ({ ...p, move_type: 'local' }))}
                        className={`flex-1 py-1.5 text-xs font-medium transition-colors ${(form?.move_type || 'local') !== 'long_distance' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'}`}
                      >
                        🏠 Local
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm(p => ({ ...p, move_type: 'long_distance' }))}
                        className={`flex-1 py-1.5 text-xs font-medium transition-colors ${form?.move_type === 'long_distance' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'}`}
                      >
                        🚛 Long Distance
                      </button>
                    </div>
                  </div>
                  {form?.move_type === 'long_distance' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Cubic Feet (CF)</label>
                        <input type="number" min="0" step="1" value={form?.cubic_feet} onChange={e => setForm(p => ({ ...p, cubic_feet: e?.target?.value }))} className={inputCls} placeholder="e.g. 500" />
                      </div>
                      <div>
                        <label className="block text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Miles</label>
                        <input type="number" min="0" step="1" value={form?.miles} onChange={e => setForm(p => ({ ...p, miles: e?.target?.value }))} className={inputCls} placeholder="e.g. 350" />
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className="block text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Notes</label>
                <textarea value={form?.notes} onChange={e => setForm(p => ({ ...p, notes: e?.target?.value }))} rows={4} className={`${inputCls} resize-none`} placeholder="Move date, special items, photo links, etc." />
              </div>
            </>
          ) : (
            <>
              {lead?.name && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Name</p>
                  <p className="text-sm text-foreground font-medium">{lead?.name}</p>
                </div>
              )}
              {lead?.phone && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Phone</p>
                  <p className="text-sm text-foreground">{lead?.phone}</p>
                </div>
              )}
              {lead?.email && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Email</p>
                  <p className="text-sm text-foreground">{lead?.email}</p>
                </div>
              )}
              {lead?.service_type && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Service Type</p>
                  <p className="text-sm text-foreground">{lead?.service_type}</p>
                </div>
              )}
              {lead?.source && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Source</p>
                  <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${SOURCE_COLORS?.[lead?.source] || 'bg-muted text-muted-foreground'}`}>
                    {lead?.source}
                  </span>
                </div>
              )}
              {lead?.status && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Status</p>
                  <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium capitalize ${STATUS_COLORS?.[lead?.status] || 'bg-muted text-muted-foreground'}`}>
                    {lead?.status}
                  </span>
                </div>
              )}
              {lead?.notes && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Notes</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap break-words">{lead?.notes}</p>
                </div>
              )}
              {lead?.pickup_address && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Pickup Address</p>
                  <p className="text-sm text-foreground">{lead?.pickup_address}</p>
                </div>
              )}
              {lead?.delivery_address && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Delivery Address</p>
                  <p className="text-sm text-foreground">{lead?.delivery_address}</p>
                </div>
              )}
              {/* Long Distance badge with CF and miles */}
              {lead?.move_type === 'long_distance' && (
                <div className="flex items-center space-x-3 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                  <span className="text-xs font-semibold text-orange-700 dark:text-orange-400">🚛 Long Distance</span>
                  {lead?.cubic_feet && (
                    <span className="text-xs text-foreground font-medium">{lead?.cubic_feet} CF</span>
                  )}
                  {lead?.miles && (
                    <span className="text-xs text-foreground font-medium">{lead?.miles} mi</span>
                  )}
                </div>
              )}
              {lead?.created_at && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Created</p>
                  <p className="text-sm text-foreground">{formatDate(lead?.created_at)}</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex space-x-3 mt-6">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving || !form?.name?.trim()}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                <Icon name="Save" size={15} />
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </>
          ) : (
            <>
              {lead?.status !== 'converted' && lead?.status !== 'lost' && (
                <button
                  onClick={() => { onConvert(lead); onClose(); }}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Icon name="UserCheck" size={15} />
                  <span>Convert to Job</span>
                </button>
              )}
              <button
                onClick={() => { onDelete(lead?.id); onClose(); }}
                className="flex items-center justify-center space-x-2 px-4 py-2 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 text-sm font-medium rounded-lg transition-colors"
              >
                <Icon name="Trash2" size={15} />
                <span>Delete</span>
              </button>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-border text-foreground text-sm font-medium rounded-lg hover:bg-muted transition-colors"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const LeadsPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [convertSaving, setConvertSaving] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [convertingLead, setConvertingLead] = useState(null); // lead pending conversion
  const [newLead, setNewLead] = useState({
    name: '', email: '', phone: '', service_type: 'General',
    source: 'Web', notes: '', assigned_to: '', custom_fields: {}
  });
  const { profile, organization } = useAuth();

  // Determine service type from org business type
  const getOrgServiceType = () => {
    if (!organization?.business_type) return 'General';
    const bt = organization?.business_type;
    const mapped = bt?.charAt(0)?.toUpperCase() + bt?.slice(1)?.replace(/_/g, ' ');
    const match = SERVICE_TYPES?.find(s => s?.toLowerCase() === mapped?.toLowerCase() || s?.toLowerCase()?.includes(bt?.toLowerCase()?.split('_')?.[0]));
    return match || 'General';
  };

  const orgServiceType = getOrgServiceType();
  const nicheFields = SERVICE_CUSTOM_FIELDS?.[orgServiceType] || [];
  const hasNicheFields = nicheFields?.length > 0;

  useEffect(() => {
    setNewLead(p => ({ ...p, service_type: orgServiceType }));
  }, [organization?.business_type]);

  const fetchLeads = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('leads')?.select('*, assigned:profiles(full_name)')?.eq('org_id', profile?.org_id)?.is('deleted_at', null)?.order('created_at', { ascending: false });
      if (error) throw error;
      setLeads(data || []);
    } catch (err) {
      toast?.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id]);

  const fetchTeam = useCallback(async () => {
    if (!profile?.org_id) return;
    const { data } = await supabase?.from('profiles')?.select('id, full_name')?.eq('org_id', profile?.org_id);
    if (data) setTeamMembers(data);
  }, [profile?.org_id]);

  useEffect(() => { fetchLeads(); fetchTeam(); }, [fetchLeads, fetchTeam]);

  const handleAddLead = async () => {
    if (!newLead?.name?.trim()) return;
    setSaving(true);
    try {
      const isMoving = (newLead?.service_type || '')?.toLowerCase()?.includes('mov');
      const { error } = await supabase?.from('leads')?.insert({
        org_id: profile?.org_id,
        name: newLead?.name,
        email: newLead?.email || null,
        phone: newLead?.phone || null,
        service_type: newLead?.service_type || 'General',
        source: newLead?.source || 'Web',
        notes: newLead?.notes || null,
        assigned_to: newLead?.assigned_to || null,
        status: 'new',
        ...(isMoving && {
          move_type: newLead?.move_type || 'local',
          cubic_feet: newLead?.move_type === 'long_distance' ? (Number(newLead?.cubic_feet) || null) : null,
          miles: newLead?.move_type === 'long_distance' ? (Number(newLead?.miles) || null) : null,
        }),
      });
      if (error) throw error;

      // Auto-log activity
      await supabase?.from('activities')?.insert({
        org_id: profile?.org_id,
        owner_id: profile?.id,
        type: 'note',
        subject: `New Lead: ${newLead?.name}`,
        description: `Lead added from ${newLead?.source || 'Unknown'} — ${newLead?.service_type || ''}`,
        due_date: new Date()?.toISOString(),
        priority: 'medium',
        status: 'pending'
      });

      // Send in-app + email notification
      try {
        const adminProfile = await getAdminProfile(profile?.org_id);
        await notify({
          org_id: profile?.org_id,
          type: 'new_lead',
          title: `New Lead: ${newLead?.name}`,
          body: `Source: ${newLead?.source || 'Unknown'} — ${newLead?.service_type || ''}`,
          link: '/leads',
          adminEmail: adminProfile?.email || null,
          orgName: organization?.name || adminProfile?.full_name || 'Wilcart',
          emailData: {
            lead_name: newLead?.name,
            lead_email: newLead?.email || '',
            lead_phone: newLead?.phone || '',
            source: newLead?.source || '',
            service_type: newLead?.service_type || '',
          },
        });
      } catch (notifErr) {
        console.error('Failed to send new lead notification:', notifErr);
      }

      toast?.success('Lead added!');
      setIsAddModalOpen(false);
      setNewLead({ name: '', email: '', phone: '', service_type: 'General', source: 'Web', notes: '', assigned_to: '', custom_fields: {} });
      fetchLeads();

      // SMS confirmation to lead if they have a phone and org has SMS enabled
      try {
        if (organization?.sms_enabled && organization?.sms_notify_new_lead && newLead?.phone) {
          const message = buildNewLeadSMS({
            leadName: newLead?.name?.split(' ')?.[0],
            serviceType: newLead?.service_type,
            orgName: organization?.name,
          });
          await sendSMS({
            to: newLead?.phone,
            message,
            orgId: profile?.org_id,
            eventType: 'new_lead',
          });
        }
      } catch (_smsErr) {
        // SMS failure should not block lead creation
      }
    } catch (err) {
      toast?.error('Failed to add lead: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    try {
      await supabase?.from('leads')?.update({ deleted_at: new Date()?.toISOString() })?.eq('id', leadId);
      toast?.success('Moved to Trash');
      fetchLeads();
    } catch (err) {
      toast?.error('Failed to delete');
    }
  };

  // Opens the ConvertLeadModal — single entry point for all conversion triggers
  const openConvertModal = (lead) => {
    setSelectedLead(null); // close detail modal if open
    setConvertingLead(lead);
  };

  // Called when user confirms conversion in ConvertLeadModal
  const handleConfirmConvert = async (jobForm) => {
    const lead = convertingLead;
    if (!lead || !profile?.org_id) return;

    setConvertSaving(true);
    try {
      // Create contact
      const nameParts = lead?.name?.trim()?.split(' ');
      const firstName = nameParts?.[0] || lead?.name;
      const lastName = nameParts?.slice(1)?.join(' ') || '';

      const { data: contactData, error: contactError } = await supabase
        ?.from('contacts')
        ?.insert({
          org_id: profile?.org_id,
          first_name: firstName,
          last_name: lastName,
          email: lead?.email || '',
          phone: lead?.phone || '',
          owner_id: profile?.id
        })
        ?.select()
        ?.single();
      if (contactError) throw contactError;

      const leadServiceType = lead?.service_type || orgServiceType;
      const jobTitle = `${leadServiceType} - ${lead?.name}`;
      const jobCustomFields = {
        ...(lead?.custom_fields || {}),
        pickup_address: lead?.pickup_address || null,
        delivery_address: lead?.delivery_address || null,
        move_from: lead?.pickup_address || lead?.custom_fields?.move_from || null,
        move_to: lead?.delivery_address || lead?.custom_fields?.move_to || null,
        source: lead?.source || null,
      };

      // Use date from the confirmation form (always set — defaults to today)
      const scheduledDate = jobForm?.scheduled_date || null;
      const scheduledTime = jobForm?.scheduled_time || '09:00';
      const laborCost = Number(jobForm?.labor_cost) || 0;
      const materialsCost = Number(jobForm?.materials_cost) || 0;
      const durationHours = Number(jobForm?.duration_hours) || 1;

      const { data: jobData, error: jobError } = await supabase
        ?.from('jobs')
        ?.insert({
          org_id: profile?.org_id,
          title: jobTitle,
          client_id: contactData?.id,
          lead_id: lead?.id,
          service_type: leadServiceType,
          status: 'scheduled',
          assigned_to: lead?.assigned_to || null,
          description: jobForm?.notes || lead?.notes || null,
          notes: jobForm?.notes || lead?.notes || null,
          location: jobForm?.location || lead?.pickup_address || null,
          scheduled_date: scheduledDate,
          scheduled_time: scheduledTime,
          labor_cost: laborCost,
          materials_cost: materialsCost,
          total_cost: laborCost + materialsCost,
          subtotal: laborCost + materialsCost,
          duration_hours: durationHours,
          custom_fields: jobCustomFields
        })
        ?.select()
        ?.single();
      if (jobError) throw jobError;

      // Auto-generate documents with price
      const jobForDocs = {
        title: jobTitle,
        service_type: leadServiceType,
        description: jobForm?.notes || lead?.notes || null,
        location: jobForm?.location || lead?.pickup_address || null,
        scheduled_date: scheduledDate,
        custom_fields: jobCustomFields,
        labor_cost: laborCost,
        materials_cost: materialsCost,
        duration_hours: durationHours,
      };
      await generateDocumentsFromLead(supabase, profile?.org_id, jobData?.id, jobForDocs, contactData, organization?.name || 'Wilcart');

      // Update lead status to converted
      await supabase?.from('leads')?.update({ status: 'converted' })?.eq('id', lead?.id);

      // Log activity
      await supabase?.from('activities')?.insert({
        org_id: profile?.org_id,
        owner_id: profile?.id,
        type: 'note',
        subject: `Lead Converted: ${lead?.name}`,
        description: `Lead converted to Contact & Job — ${leadServiceType || ''}`,
        due_date: new Date()?.toISOString(),
        priority: 'high',
        status: 'completed'
      });

      toast?.success(`✅ ${lead?.name} converted! Job created & added to calendar.`);
      setConvertingLead(null);
      fetchLeads();
    } catch (err) {
      toast?.error('Conversion failed: ' + err?.message);
    } finally {
      setConvertSaving(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result?.destination) return;
    const { draggableId, destination } = result;
    const newStatus = destination?.droppableId;

    setLeads(prev => prev?.map(l => l?.id === draggableId ? { ...l, status: newStatus } : l));

    try {
      await supabase?.from('leads')?.update({ status: newStatus })?.eq('id', draggableId);
    } catch (err) {
      toast?.error('Failed to update status');
      fetchLeads();
    }
  };

  const getColumnLeads = (status) => leads?.filter(l => l?.status === status);

  return (
    <>
      <Helmet>
        <title>Leads - Wilcart CRM</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Leads</h1>
                <p className="text-muted-foreground mt-1">{leads?.length} leads</p>
              </div>
              <Button onClick={() => setIsAddModalOpen(true)} className="mt-4 sm:mt-0">
                <Icon name="Plus" size={16} className="mr-2" />
                Add Lead
              </Button>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {[1,2,3,4,5]?.map(i => <div key={i} className="h-64 bg-muted animate-pulse rounded-xl" />)}
              </div>
            ) : (
              <DragDropContext onDragEnd={async (result) => {
                if (!result?.destination) return;
                const { draggableId, destination } = result;
                const newStatus = destination?.droppableId;
                setLeads(prev => prev?.map(l => l?.id === draggableId ? { ...l, status: newStatus } : l));
                await supabase?.from('leads')?.update({ status: newStatus })?.eq('id', draggableId);
              }}>
                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {COLUMNS?.map(col => (
                    <div key={col?.id} className="flex flex-col">
                      <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg mb-3 ${col?.color}`}>
                        <div className={`w-2 h-2 rounded-full ${col?.dot}`} />
                        <span className="text-xs font-semibold">{col?.label}</span>
                        <span className="ml-auto text-xs font-medium">{leads?.filter(l => l?.status === col?.id)?.length}</span>
                      </div>
                      <Droppable droppableId={col?.id}>
                        {(provided) => (
                          <div ref={provided?.innerRef} {...provided?.droppableProps} className="flex-1 min-h-24">
                            {leads?.filter(l => l?.status === col?.id)?.map((lead, index) => (
                              <LeadCard
                                key={lead?.id}
                                lead={lead}
                                index={index}
                                onClick={(l) => setSelectedLead(l)}
                                onConvert={openConvertModal}
                                onDelete={handleDeleteLead}
                              />
                            ))}
                            {provided?.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  ))}
                </div>
              </DragDropContext>
            )}
          </div>
        </main>

        {/* Lead Detail Modal */}
        {selectedLead && (
          <LeadDetailModal
            lead={selectedLead}
            onClose={() => setSelectedLead(null)}
            onUpdate={(updatedLead) => {
              setLeads(prev => prev?.map(l => l?.id === updatedLead?.id ? { ...l, ...updatedLead } : l));
              setSelectedLead(updatedLead);
            }}
            onConvert={openConvertModal}
            onDelete={handleDeleteLead}
          />
        )}

        {/* Convert Lead Modal */}
        {convertingLead && (
          <ConvertLeadModal
            lead={convertingLead}
            saving={convertSaving}
            onClose={() => setConvertingLead(null)}
            onConfirm={handleConfirmConvert}
          />
        )}

        {/* Add Lead Modal */}
        {isAddModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">Add Lead</h2>
                <button onClick={() => setIsAddModalOpen(false)} className="text-muted-foreground hover:text-foreground">
                  <Icon name="X" size={20} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Full Name *</label>
                  <input type="text" value={newLead?.name} onChange={e => setNewLead(p => ({ ...p, name: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                    <input type="email" value={newLead?.email} onChange={e => setNewLead(p => ({ ...p, email: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Phone</label>
                    <input type="tel" value={newLead?.phone} onChange={e => setNewLead(p => ({ ...p, phone: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Source</label>
                    <select value={newLead?.source} onChange={e => setNewLead(p => ({ ...p, source: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                      {SOURCES?.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Assign To</label>
                    <select value={newLead?.assigned_to} onChange={e => setNewLead(p => ({ ...p, assigned_to: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                      <option value="">Unassigned</option>
                      {teamMembers?.map(m => <option key={m?.id} value={m?.id}>{m?.full_name}</option>)}
                    </select>
                  </div>
                </div>

                {/* Niche-specific fields — shown based on org business type */}
                {hasNicheFields && (
                  <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg space-y-3">
                    <p className="text-xs font-semibold text-primary">{orgServiceType} — Lead Details</p>
                    {nicheFields?.map(field => (
                      <div key={field?.key}>
                        <label className="block text-xs font-medium text-foreground mb-1">{field?.label}</label>
                        {field?.type === 'select' ? (
                          <select
                            value={newLead?.custom_fields?.[field?.key] || ''}
                            onChange={e => setNewLead(p => ({ ...p, custom_fields: { ...p?.custom_fields, [field?.key]: e?.target?.value } }))}
                            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                          >
                            <option value="">Select...</option>
                            {field?.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        ) : (
                          <input type={field?.type} placeholder={field?.placeholder || ''}
                            value={newLead?.custom_fields?.[field?.key] || ''}
                            onChange={e => setNewLead(p => ({ ...p, custom_fields: { ...p?.custom_fields, [field?.key]: e?.target?.value } }))}
                            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background resize-none" />
                        )}
                      </div>
                    ))}
                    {/* Move Type + Long Distance fields for Moving leads */}
                    {orgServiceType === 'Moving' && (
                      <div className="space-y-2 pt-1 border-t border-primary/20">
                        <div>
                          <label className="block text-xs font-medium text-foreground mb-1">Move Type</label>
                          <div className="flex rounded-lg border border-border overflow-hidden">
                            <button
                              type="button"
                              onClick={() => setNewLead(p => ({ ...p, move_type: 'local' }))}
                              className={`flex-1 py-1.5 text-xs font-medium transition-colors ${(newLead?.move_type || 'local') !== 'long_distance' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'}`}
                            >
                              🏠 Local
                            </button>
                            <button
                              type="button"
                              onClick={() => setNewLead(p => ({ ...p, move_type: 'long_distance' }))}
                              className={`flex-1 py-1.5 text-xs font-medium transition-colors ${newLead?.move_type === 'long_distance' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground'}`}
                            >
                              🚛 Long Distance
                            </button>
                          </div>
                        </div>
                        {newLead?.move_type === 'long_distance' && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-foreground mb-1">Cubic Feet (CF)</label>
                              <input type="number" min="0" step="1" placeholder="e.g. 500"
                                value={newLead?.cubic_feet || ''}
                                onChange={e => setNewLead(p => ({ ...p, cubic_feet: e?.target?.value }))}
                                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-foreground mb-1">Miles</label>
                              <input type="number" min="0" step="1" placeholder="e.g. 350"
                                value={newLead?.miles || ''}
                                onChange={e => setNewLead(p => ({ ...p, miles: e?.target?.value }))}
                                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Notes</label>
                  <textarea value={newLead?.notes} onChange={e => setNewLead(p => ({ ...p, notes: e?.target?.value }))}
                    rows={2} className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background resize-none" />
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
                <Button className="flex-1" onClick={handleAddLead} loading={saving} disabled={saving || !newLead?.name?.trim()}>
                  {saving ? 'Adding...' : 'Add Lead'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LeadsPage;
